import { useCallback, useEffect, useMemo, useState } from "react";
import {
  useAttrs,
  useChainedCommands,
  useCurrentSelection,
  useEditorEvent,
} from "@remirror/react";
import { useLinkShortcut } from "./useLinkShortcut";

export function useLinkState(nodeId: string) {
  const chain = useChainedCommands();

  const { isEditing, linkShortcut, setIsEditing } = useLinkShortcut(nodeId);
  const { from, to, empty } = useCurrentSelection();

  const url = (useAttrs().link()?.href as string) ?? "";
  const [href, setHref] = useState<string>(url);

  useEffect(() => {
    setHref(url);
  }, [url]);

  const onRemove = useCallback(() => chain.removeLink().focus().run(), [chain]);

  const handleOnFocus = useCallback(() => {
    if (!isEditing) {
      return;
    }

    setIsEditing(false);
  }, [isEditing, setIsEditing]);

  useEditorEvent("focus", handleOnFocus);

  const submitHref = useCallback(() => {
    setIsEditing(false);
    const range = linkShortcut ?? undefined;

    if (href === "") {
      chain.removeLink();
    } else {
      if (from === to) {
        // No selection, insert the link as text at the cursor position
        chain.insertText(href);
      } else {
        // Selection exists, update the link in the selected range
        chain.updateLink({ href, auto: false }, range);
      }
    }

    chain.focus(range?.to ?? to).run();
  }, [setIsEditing, linkShortcut, chain, href, from, to]);

  const cancelHref = useCallback(() => {
    setIsEditing(false);
  }, [setIsEditing]);

  const clickEdit = useCallback(() => {
    if (empty) {
      chain.selectLink();
    }

    setIsEditing(true);
  }, [chain, empty, setIsEditing]);

  return useMemo(
    () => ({
      href,
      setHref,
      isEditing,
      clickEdit,
      onRemove,
      submitHref,
      cancelHref,
    }),
    [href, isEditing, clickEdit, onRemove, submitHref, cancelHref],
  );
}
